/**
 * Capacità dichiarate al server (API.md) e piattaforma.
 *  - max_height 2160 solo se lo schermo è davvero 4K (screen.width * devicePixelRatio >= 3840) E il browser
 *    dichiara il supporto HEVC; altrimenti 1080.
 *  - codecs: ["hevc","h264"] o ["h264"] (nel dubbio h264).
 * Nota: `isTypeSupported`/`canPlayType` dicono "decodificabile", non "in hardware": è la miglior stima
 * disponibile da un browser; sul campo va verificata sul device.
 */
import type { Capabilities, Platform } from './types';

export interface CapabilityEnv {
  screenWidth: number;
  devicePixelRatio: number;
  mediaSourceSupports?: (mime: string) => boolean;
  canPlayType?: (mime: string) => string;
}

export function detectCapabilities(env: CapabilityEnv): Capabilities {
  const hevcMime = 'video/mp4; codecs="hvc1.1.6.L150.B0"';
  let hevc = false;
  try {
    hevc = env.mediaSourceSupports?.(hevcMime) === true
      || (env.canPlayType?.('video/mp4; codecs="hvc1"') ?? '') !== '';
  } catch {
    hevc = false;
  }
  const physicalWidth = env.screenWidth * (env.devicePixelRatio || 1);
  const is4k = physicalWidth >= 3840;
  return {
    max_height: is4k && hevc ? 2160 : 1080,
    codecs: hevc ? ['hevc', 'h264'] : ['h264'],
  };
}

/**
 * Tetto imposto dalla configurazione (`?maxh=`): il browser può dichiarare HEVC senza decodificarlo bene in
 * hardware (es. Chromium su Raspberry Pi 4). Con tetto ≤ 1080 si chiede solo H.264.
 */
export function capCapabilities(caps: Capabilities, maxHeight?: Capabilities['max_height']): Capabilities {
  if (!maxHeight || maxHeight >= caps.max_height) return caps;
  return { max_height: 1080, codecs: caps.codecs.filter((c) => c === 'h264') };
}

export function detectCapabilitiesFromBrowser(): Capabilities {
  let supports: ((m: string) => boolean) | undefined;
  const ms = (globalThis as { MediaSource?: { isTypeSupported?: (m: string) => boolean } }).MediaSource;
  if (ms && typeof ms.isTypeSupported === 'function') supports = (m) => ms.isTypeSupported!(m);
  const probe = document.createElement('video');
  return detectCapabilities({
    screenWidth: window.screen.width,
    devicePixelRatio: window.devicePixelRatio,
    mediaSourceSupports: supports,
    canPlayType: (m) => probe.canPlayType(m),
  });
}

export function detectPlatform(w: object = window): Platform {
  if ('tizen' in w) return 'tizen';
  if ('webOS' in w) return 'webos';
  return 'web';
}
